import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrdenesPermisos } from '../../hooks/useOrdenesPermisos';
import { ordenesConfigAPI } from '../../lib/ordenesApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
    Settings2,
    ArrowLeft,
    Plus,
    Trash2,
    Loader2,
    Type,
    Hash,
    Calendar,
    ToggleLeft,
    List,
    GripVertical,
} from 'lucide-react';

const ENTIDADES = [
    { id: 'cliente', nombre: 'Clientes', descripcion: 'Campos adicionales para empresas/clientes' },
    { id: 'vehiculo', nombre: 'Vehículos', descripcion: 'Campos adicionales para vehículos' },
    { id: 'orden', nombre: 'Órdenes', descripcion: 'Campos adicionales para órdenes de servicio' },
];

const TIPOS_CAMPO = [
    { id: 'texto', nombre: 'Texto', icon: Type, descripcion: 'Campo de texto libre' },
    { id: 'numero', nombre: 'Número', icon: Hash, descripcion: 'Campo numérico' },
    { id: 'fecha', nombre: 'Fecha', icon: Calendar, descripcion: 'Selector de fecha' },
    { id: 'booleano', nombre: 'Sí/No', icon: ToggleLeft, descripcion: 'Checkbox verdadero/falso' },
    { id: 'seleccion', nombre: 'Selección', icon: List, descripcion: 'Lista desplegable con opciones' },
];

const initialFormState = {
    entidad: '',
    nombre: '',
    etiqueta: '',
    tipo: 'texto',
    requerido: false,
    opciones: '',
};

export default function OrdenesCamposConfigPage() {
    const navigate = useNavigate();
    const { tienePermiso, esAdmin, loading: permisosLoading } = useOrdenesPermisos();
    const [activeTab, setActiveTab] = useState('cliente');
    const [loading, setLoading] = useState(true);
    const [campos, setCampos] = useState({});
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [editingCampo, setEditingCampo] = useState(null);
    const [formData, setFormData] = useState(initialFormState);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadCampos();
    }, []);

    const loadCampos = async () => {
        try {
            setLoading(true);
            const camposPorEntidad = {};
            for (const entidad of ENTIDADES) {
                const res = await ordenesConfigAPI.getCampos(entidad.id);
                camposPorEntidad[entidad.id] = res.data;
            }
            setCampos(camposPorEntidad);
        } catch (err) {
            console.error('Error loading campos:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setEditingCampo(null);
        setFormData({
            ...initialFormState,
            entidad: activeTab,
        });
        setShowDialog(true);
    };

    const handleOpenDelete = (campo) => {
        setEditingCampo(campo);
        setShowDeleteDialog(true);
    };

    const handleSave = async () => {
        if (!formData.nombre || !formData.etiqueta) {
            toast.error('Nombre y Etiqueta son requeridos');
            return;
        }

        // Validar nombre (solo letras, números y guiones bajos)
        if (!/^[a-z][a-z0-9_]*$/.test(formData.nombre)) {
            toast.error('El nombre debe comenzar con letra minúscula y solo contener letras, números y guiones bajos');
            return;
        }

        setSaving(true);
        try {
            const dataToSend = {
                entidad: formData.entidad,
                nombre_campo: formData.nombre,  // Backend expects nombre_campo
                etiqueta: formData.etiqueta,
                tipo: formData.tipo,
                requerido: formData.requerido,
                opciones: formData.tipo === 'seleccion' && formData.opciones 
                    ? formData.opciones.split(',').map(o => o.trim()).filter(Boolean)
                    : null,
            };
            
            await ordenesConfigAPI.createCampo(dataToSend);
            toast.success('Campo creado correctamente');
            setShowDialog(false);
            loadCampos();
        } catch (err) {
            const errorMsg = err.response?.data?.detail;
            toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error al crear campo');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            await ordenesConfigAPI.deleteCampo(editingCampo.id);
            toast.success('Campo eliminado');
            setShowDeleteDialog(false);
            loadCampos();
        } catch (err) {
            toast.error('Error al eliminar campo');
        } finally {
            setSaving(false);
        }
    };

    const getTipoInfo = (tipo) => {
        return TIPOS_CAMPO.find(t => t.id === tipo) || TIPOS_CAMPO[0];
    };

    if (permisosLoading || loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    if (!esAdmin && !tienePermiso('configurar_campos')) {
        return (
            <div className="p-6 text-center">
                <Settings2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h2 className="text-xl font-bold mb-2">Acceso Restringido</h2>
                <p className="text-muted-foreground">No tienes permisos para configurar campos.</p>
                <Button variant="outline" className="mt-4" onClick={() => navigate('/ordenes/configuracion')}>
                    Volver a Configuración
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/configuracion')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Settings2 className="h-6 w-6" />
                            Campos Personalizados
                        </h1>
                        <p className="text-muted-foreground">Agrega campos adicionales a entidades</p>
                    </div>
                </div>
                <Button onClick={handleOpenCreate} data-testid="add-field-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Campo
                </Button>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    {ENTIDADES.map(entidad => (
                        <TabsTrigger key={entidad.id} value={entidad.id}>
                            {entidad.nombre}
                            {campos[entidad.id]?.length > 0 && (
                                <Badge variant="secondary" className="ml-2">
                                    {campos[entidad.id].length}
                                </Badge>
                            )}
                        </TabsTrigger>
                    ))}
                </TabsList>

                {ENTIDADES.map(entidad => (
                    <TabsContent key={entidad.id} value={entidad.id} className="space-y-4">
                        <Card className="border-2">
                            <CardHeader>
                                <CardTitle>{entidad.nombre}</CardTitle>
                                <CardDescription>{entidad.descripcion}</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {campos[entidad.id]?.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <Settings2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                        <p>No hay campos personalizados para {entidad.nombre.toLowerCase()}</p>
                                        <Button variant="outline" className="mt-4" onClick={handleOpenCreate}>
                                            <Plus className="h-4 w-4 mr-2" />
                                            Agregar primer campo
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {campos[entidad.id]?.map((campo) => {
                                            const tipoInfo = getTipoInfo(campo.tipo);
                                            const TipoIcon = tipoInfo.icon;
                                            
                                            return (
                                                <div 
                                                    key={campo.id}
                                                    className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg"
                                                >
                                                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                                                    <div className="p-2 rounded-lg bg-background">
                                                        <TipoIcon className="h-4 w-4" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-medium">{campo.etiqueta}</p>
                                                            {campo.requerido && (
                                                                <Badge variant="destructive" className="text-xs">Requerido</Badge>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-muted-foreground">
                                                            <span className="font-mono">{campo.nombre_campo}</span> • {tipoInfo.nombre}
                                                            {campo.opciones?.length > 0 && (
                                                                <span> • {campo.opciones.length} opciones</span>
                                                            )}
                                                        </p>
                                                    </div>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        onClick={() => handleOpenDelete(campo)}
                                                    >
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                ))}
            </Tabs>

            {/* Create Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Agregar Campo Personalizado</DialogTitle>
                        <DialogDescription>
                            Define un nuevo campo para {ENTIDADES.find(e => e.id === formData.entidad)?.nombre?.toLowerCase() || 'la entidad'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Entidad</Label>
                            <Select value={formData.entidad} onValueChange={(v) => setFormData({...formData, entidad: v})}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar entidad" />
                                </SelectTrigger>
                                <SelectContent>
                                    {ENTIDADES.map(e => (
                                        <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Nombre técnico *</Label>
                            <Input
                                value={formData.nombre}
                                onChange={(e) => setFormData({...formData, nombre: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                                placeholder="numero_contrato"
                            />
                            <p className="text-xs text-muted-foreground">Solo letras minúsculas, números y guiones bajos</p>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Etiqueta visible *</Label>
                            <Input
                                value={formData.etiqueta}
                                onChange={(e) => setFormData({...formData, etiqueta: e.target.value})}
                                placeholder="Número de Contrato"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Tipo de campo</Label>
                            <Select value={formData.tipo} onValueChange={(v) => setFormData({...formData, tipo: v})}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {TIPOS_CAMPO.map(tipo => (
                                        <SelectItem key={tipo.id} value={tipo.id}>
                                            <div className="flex items-center gap-2">
                                                <tipo.icon className="h-4 w-4" />
                                                {tipo.nombre}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {formData.tipo === 'seleccion' && (
                            <div className="space-y-2">
                                <Label>Opciones</Label>
                                <Input
                                    value={formData.opciones}
                                    onChange={(e) => setFormData({...formData, opciones: e.target.value})}
                                    placeholder="Opción 1, Opción 2, Opción 3"
                                />
                                <p className="text-xs text-muted-foreground">Separar opciones con comas</p>
                            </div>
                        )}
                        
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="requerido"
                                checked={formData.requerido}
                                onCheckedChange={(v) => setFormData({...formData, requerido: v})}
                            />
                            <label htmlFor="requerido" className="text-sm cursor-pointer">
                                Campo requerido (obligatorio)
                            </label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Crear Campo
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Eliminar Campo</DialogTitle>
                        <DialogDescription>
                            ¿Estás seguro de eliminar el campo <strong>{editingCampo?.etiqueta}</strong>? 
                            Los datos guardados en este campo se perderán.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Eliminar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
